import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { IconButton, Menu, Skeleton, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { LuCheck, LuTrash2 } from 'react-icons/lu';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReducedMotion } from 'motion/react';
import { useToast } from '@/app/context/toast-provider';
import { MenuGrowTransition, ScrollShadow } from '@/components/ui';
import { deleteNotificationsIdMutation, getNotificationsOptions, getNotificationsQueryKey, putNotificationsMutation } from '@/generated/api-client/@tanstack/react-query.gen';
import { invalidateOperation } from '@/lib/api/query-invalidation';
import { getNotificationKind, NOTIFICATION_FILTERS } from './NotificationsPanel.kinds';

/** The unread count (sidebar badge, find-talent toast) refreshes this often, and whenever the tab becomes visible. */
const UNREAD_POLL_MS = 15_000;

/** Hover tints only where hover exists; on touch they'd stick after a tap. */
const CAN_HOVER = '@media (hover: hover) and (pointer: fine)';
const NotificationsPanel = ({
  isOpen = false,
  onClose,
  anchorEl,
  onCountChange
}) => {
  const {
    showSuccessToast
  } = useToast();
  const {
    isLoading: isUserLoading,
    user
  } = useUser();
  const signedIn = Boolean(user) && !isUserLoading;
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const listQuery = useQuery({
    ...getNotificationsOptions(),
    enabled: signedIn,
    // One operation, two shapes: without `unreadCount` it's the list.
    select: res => 'data' in res ? res.data : []
  });
  const notifications = listQuery.data ?? [];

  // App defaults are 30s staleTime and no focus refetch; the badge keeps polling (also in a background tab, as
  // before) and refreshes on tab focus regardless.
  const unreadQuery = useQuery({
    ...getNotificationsOptions({
      query: {
        unreadCount: 'true'
      }
    }),
    enabled: signedIn,
    staleTime: 0,
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: 'always'
  });

  // Fresh list each time the panel opens (the first load happens on mount).
  const wasOpenRef = useRef(isOpen);
  const {
    refetch: refetchList
  } = listQuery;
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && signedIn) void refetchList();
    wasOpenRef.current = isOpen;
  }, [isOpen, signedIn, refetchList]);
  useEffect(() => {
    if (!signedIn) onCountChange?.(0);
  }, [signedIn, onCountChange]);
  useEffect(() => {
    if (listQuery.data) onCountChange?.(listQuery.data.filter(n => !n.isRead).length);
  }, [listQuery.data, onCountChange]);

  // Toast a finished find-talent search once: the first poll only records a baseline.
  const hasBaselineRef = useRef(false);
  const lastShownNotificationIdRef = useRef(null);
  useEffect(() => {
    const summary = unreadQuery.data;
    if (!summary || !('count' in summary)) return;
    const latest = summary.latestNotification;
    if (latest && latest.link?.includes('tab=find-talent')) {
      if (hasBaselineRef.current && latest.id !== lastShownNotificationIdRef.current) showSuccessToast(latest.message);
      lastShownNotificationIdRef.current = latest.id;
    }
    hasBaselineRef.current = true;
    onCountChange?.(summary.count);
  }, [unreadQuery.data, onCountChange, showSuccessToast]);
  const markReadMutation = useMutation(putNotificationsMutation());
  const deleteMutation = useMutation(deleteNotificationsIdMutation());
  // List and unread count share the operation, so both refresh.
  const refreshNotifications = () => invalidateOperation(queryClient, 'getNotifications');

  /**
   * Applies an action to the cached list and unread count straight away (the click registers before the round
   * trip), and returns a rollback for when the request fails. In-flight loads are cancelled so they can't overwrite it.
   */
  const applyOptimistically = async update => {
    const listKey = getNotificationsQueryKey();
    const unreadKey = getNotificationsQueryKey({
      query: {
        unreadCount: 'true'
      }
    });
    await queryClient.cancelQueries({
      queryKey: listKey
    });
    await queryClient.cancelQueries({
      queryKey: unreadKey
    });
    const previousList = queryClient.getQueryData(listKey);
    const previousUnread = queryClient.getQueryData(unreadKey);
    if (previousList && 'data' in previousList) {
      const next = update(previousList.data);
      queryClient.setQueryData(listKey, {
        ...previousList,
        data: next
      });
      if (previousUnread && 'count' in previousUnread) {
        const unreadRemoved = previousList.data.filter(n => !n.isRead).length - next.filter(n => !n.isRead).length;
        queryClient.setQueryData(unreadKey, {
          ...previousUnread,
          count: Math.max(0, previousUnread.count - unreadRemoved)
        });
      }
    }
    return () => {
      queryClient.setQueryData(listKey, previousList);
      queryClient.setQueryData(unreadKey, previousUnread);
    };
  };

  // Rows marked read while the panel is open stay on the Unread tab (dimmed) until it closes, so the list doesn't
  // jump under the pointer between clicks. "Mark all read" clears the tab at once.
  const [keptOnUnread, setKeptOnUnread] = useState(new Set());
  const handleClose = () => {
    setKeptOnUnread(new Set());
    onClose();
  };
  const markAllAsRead = async () => {
    setKeptOnUnread(new Set());
    const rollback = await applyOptimistically(list => list.map(n => ({
      ...n,
      isRead: true
    })));
    try {
      await markReadMutation.mutateAsync({});
      showSuccessToast('All notifications marked as read');
      onCountChange?.(0);
      void refreshNotifications();
    } catch (err) {
      rollback();
      console.error('Error marking notifications as read: ', err);
    }
  };
  const markAsRead = async notificationId => {
    setKeptOnUnread(prev => new Set(prev).add(notificationId));
    const rollback = await applyOptimistically(list => list.map(n => n.id === notificationId ? {
      ...n,
      isRead: true
    } : n));
    try {
      await markReadMutation.mutateAsync({
        query: {
          notificationId: String(notificationId)
        }
      });
      void refreshNotifications();
    } catch (err) {
      rollback();
      console.error('Error marking notification as read: ', err);
    }
  };
  const deleteNotification = async notificationId => {
    const rollback = await applyOptimistically(list => list.filter(n => n.id !== notificationId));
    try {
      await deleteMutation.mutateAsync({
        path: {
          id: String(notificationId)
        }
      });
      showSuccessToast('Notification deleted');
      void refreshNotifications();
    } catch (err) {
      rollback();
      console.error('Error deleting notification: ', err);
    }
  };

  // First load only; reopening keeps the current list on screen while it refreshes.
  const isLoading = listQuery.isLoading;
  // A still skeleton reads as loading just as well under reduced motion.
  const skeletonAnimation = reduceMotion ? false : 'pulse';
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const [tab, setTab] = useState('unread');
  const [filter, setFilter] = useState(null);
  const inTab = tab === 'unread' ? notifications.filter(n => !n.isRead || keptOnUnread.has(n.id)) : notifications;
  const visible = filter ? inTab.filter(n => getNotificationKind(n).filter === filter) : inTab;
  const countFor = value => inTab.filter(n => getNotificationKind(n).filter === value).length;
  const openNotification = notification => {
    if (notification.link) window.open(notification.link, '_blank');
    if (!notification.isRead) void markAsRead(notification.id);
  };
  return <Menu id="notification-menu" open={isOpen} onClose={handleClose} MenuListProps={{
    'aria-labelledby': 'basic-button',
    sx: {
      py: 0,
      maxHeight: 'none',
      overflow: 'visible'
    }
  }} anchorEl={anchorEl}
  // The app's menu motion (200ms ease-out, 0.95 → 1 from the trigger corner) instead of MUI's ~350ms Grow.
  slots={{
    transition: MenuGrowTransition
  }} transformOrigin={{
    horizontal: 'left',
    vertical: 'top'
  }} anchorOrigin={{
    horizontal: 'right',
    vertical: 'top'
  }} sx={{
    '& .MuiPaper-root': {
      borderRadius: '12px',
      mt: 1,
      border: '1px solid var(--border)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    }
  }}>
            {/* pb-3: a fixed white strip under the list, so the bottom border never sits on a row. */}
            <div className="flex max-h-[480px] w-[420px] flex-col overflow-hidden pb-3">
                <div className="shrink-0 border-b border-border">
                    <div className="flex items-center justify-between gap-3 px-4 pt-3">
                        <p className="text-[15px] font-semibold text-foreground">Notifications</p>
                        {unreadCount > 0 && <button type="button" onClick={markAllAsRead} className="rounded-md px-2 py-1 text-[13px] font-medium text-foreground-muted transition-colors duration-150 ease-out hover:bg-hover hover:text-foreground">
                                Mark all read
                            </button>}
                    </div>
                    <div role="tablist" aria-label="Notifications" className="mt-2 flex gap-4 px-4">
                        {[['unread', 'Unread', unreadCount], ['all', 'All', notifications.length]].map(([value, label, count]) => <button key={value} type="button" role="tab" aria-selected={tab === value}
          // The count badge would otherwise run into the label ("Unread1").
          aria-label={`${label} (${count})`} onClick={() => setTab(value)} className={clsx('-mb-px flex items-center gap-1.5 border-b-2 pb-2 text-[13px] font-medium', tab === value ? 'border-brand text-foreground' : 'border-transparent text-foreground-subtle hover:text-foreground')}>
                                {label}
                                <span className={clsx('rounded px-1 text-[11px]', tab === value ? 'bg-brand-soft text-brand' : 'bg-muted text-foreground-subtle')}>
                                    {count}
                                </span>
                            </button>)}
                    </div>
                </div>

                {/* Edge shadows show there's more above or below the fold. */}
                <ScrollShadow axis="y" className="min-h-0 flex-1" data-testid="notifications/list">
                    <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-1">
                        {NOTIFICATION_FILTERS.map(option => {
            const active = filter === option.value;
            const count = countFor(option.value);
            return <button key={option.value} type="button" aria-pressed={active} onClick={() => setFilter(active ? null : option.value)} className={clsx('rounded-full border px-2.5 py-1 text-[12px] font-medium transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.97]', active ? 'border-brand bg-brand-soft text-brand' : 'border-border text-foreground-muted hover:border-(--border-hover)')}>
                                    {option.label}
                                    {count > 0 && <span className="ml-1 text-foreground-subtle">{count}</span>}
                                </button>;
          })}
                    </div>

                    {isLoading &&
        // Skeleton rows in the shape of a notification (icon, title, message, time).
        <div role="status" aria-label="Loading notifications" className="flex flex-col py-1">
                            {[0, 1, 2].map(row => <div key={row} className="flex gap-3 px-4 py-2.5">
                                    <Skeleton animation={skeletonAnimation} variant="rounded" width={32} height={32} sx={{
              flexShrink: 0,
              borderRadius: '8px'
            }} />
                                    <div className="min-w-0 flex-1">
                                        <Skeleton animation={skeletonAnimation} width="55%" height={18} />
                                        <Skeleton animation={skeletonAnimation} width="92%" height={16} />
                                        <Skeleton animation={skeletonAnimation} width={72} height={16} />
                                    </div>
                                </div>)}
                        </div>}

                    {!isLoading && visible.length === 0 && <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
                            <p className="text-[14px] font-medium text-foreground">
                                {notifications.length === 0 ? 'No notifications yet' : tab === 'unread' ? 'Nothing unread' : 'No notifications here'}
                            </p>
                            <p className="mt-1 text-[13px] text-foreground-subtle">
                                {notifications.length === 0 ? 'Updates will appear here' : filter ? 'Try another filter.' : 'You’re all caught up.'}
                            </p>
                            {tab === 'unread' && notifications.length > 0 && !filter && <button type="button" onClick={() => setTab('all')} className="mt-2 text-[13px] font-medium text-brand hover:underline">
                                    Show all notifications
                                </button>}
                        </div>}

                    {!isLoading && visible.length > 0 && <ul className="flex flex-col py-1">
                            {visible.map(notification => {
            const kind = getNotificationKind(notification);
            const Icon = kind.icon;
            return <li key={notification.id}
            // Divider indented to the text column (16px padding + 32px icon + 12px gap).
            className="group relative flex gap-3 px-4 py-2.5 select-none transition-colors duration-150 ease-out after:absolute after:right-0 after:bottom-0 after:left-[60px] after:h-px after:bg-border/70 last:after:hidden hover:bg-hover focus-within:bg-hover">
                                        <span className={clsx('relative grid size-8 shrink-0 place-items-center rounded-lg', notification.isRead ? 'bg-muted text-foreground-subtle' : 'bg-brand-soft text-brand')} aria-hidden>
                                            <Icon size={15} />
                                            {/* Unread dot on the icon's corner, ringed in the page colour so it reads as a badge. */}
                                            {!notification.isRead && <span className="absolute -top-px -right-px size-2 rounded-full bg-brand ring-[1.5px] ring-page" />}
                                        </span>
                                        {!notification.isRead && <span className="sr-only">Unread</span>}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <p className={clsx('truncate text-[13px]', notification.isRead ? 'text-foreground-muted' : 'font-semibold text-foreground')}>
                                                    {kind.label}
                                                </p>
                                                <span className="shrink-0 text-[11px] text-foreground-subtle">
                                                    {moment(notification.createdAt).fromNow(true)}
                                                </span>
                                            </div>
                                            <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug whitespace-pre-line text-foreground-muted">
                                                {notification.message}
                                            </p>
                                            <div className="mt-1.5 flex items-center justify-between">
                                                {notification.link ? <button type="button" onClick={() => openNotification(notification)} className="rounded-md text-[12px] font-semibold text-brand underline-offset-2 hover:underline">
                                                        {kind.action}
                                                    </button> : <span />}
                                                <div className="flex gap-0.5">
                                                    {!notification.isRead && <Tooltip title="Mark as read" arrow>
                                                            <IconButton size="small" disableRipple aria-label="Mark as read" onClick={() => markAsRead(notification.id)} sx={{
                        p: 0.5,
                        color: 'var(--foreground-subtle)',
                        [CAN_HOVER]: {
                          '&:hover': {
                            color: 'var(--color-brand)'
                          }
                        }
                      }}>
                                                                <LuCheck size={14} />
                                                            </IconButton>
                                                        </Tooltip>}
                                                    <Tooltip title="Delete" arrow>
                                                        <IconButton size="small" disableRipple aria-label="Delete" onClick={() => deleteNotification(notification.id)} sx={{
                        p: 0.5,
                        color: 'var(--foreground-subtle)',
                        [CAN_HOVER]: {
                          '&:hover': {
                            color: 'var(--color-error)'
                          }
                        }
                      }}>
                                                            <LuTrash2 size={14} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </li>;
          })}
                        </ul>}
                </ScrollShadow>
            </div>
        </Menu>;
};
export default NotificationsPanel;